import config from '../config';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { compareAndSwapRecord, readStoredRecord, storageKey } from './durableStore';

export const ONLINE_ONLY_MESSAGE = 'Online only — labor history needs review. Continue charting while connected. Saved device changes are retained for review and will not sync automatically.';
export const eligibilityError = () => Object.assign(new Error(ONLINE_ONLY_MESSAGE), { code: 'chart_online_only' });
const checking = new Map();
const assertOwner = owner => {
  if (getOfflineOwnerIdentity()?.ownerKey !== owner.ownerKey) throw new Error('Practice access changed. Reopen this chart.');
};
export async function readChartEligibility(clientReferenceId) {
  const owner = getOfflineOwnerIdentity();
  if (!owner || !clientReferenceId) return null;
  const value = await readStoredRecord(await storageKey(owner.ownerKey, 'offline-eligibility', clientReferenceId));
  assertOwner(owner);
  return value;
}
export async function rememberChartEligibility(clientReferenceId, eligible) {
  const owner = getOfflineOwnerIdentity();
  if (!owner || !clientReferenceId) return null;
  const key = await storageKey(owner.ownerKey, 'offline-eligibility', clientReferenceId);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const old = await readStoredRecord(key);
    assertOwner(owner);
    // A late successful snapshot/eligibility response cannot undo a newer hold.
    const nextEligible = old?.eligible === false ? false : eligible;
    const record = { owner: owner.ownerKey, kind: 'offline-eligibility', eligible: nextEligible, checkedAt: Date.now() };
    if (await compareAndSwapRecord(key, old?.storageRevision || 0, record)) {
      if (old?.eligible !== nextEligible) window.dispatchEvent(new CustomEvent('vela:chart-eligibility-changed'));
      return record;
    }
  }
  throw new Error('Offline access could not be checked on this device. Keep this chart online.');
}
export async function checkChartEligibility(clientReferenceId, { force = false } = {}) {
  const owner = getOfflineOwnerIdentity();
  if (!owner || !clientReferenceId) return null;
  const stored = await readChartEligibility(clientReferenceId);
  if (!navigator.onLine) return stored;
  // Holds are sticky. The server currently has no release action; reconciliation never
  // silently re-enrolls a chart or submits its retained draft.
  if (stored?.eligible === false || (!force && stored?.checkedAt > Date.now() - 60000)) return stored;
  const key = `${owner.ownerKey}:${clientReferenceId}`;
  if (checking.has(key)) return checking.get(key);
  const task = (async () => {
    const response = await fetch(`${config.api.url}/clients/${encodeURIComponent(clientReferenceId)}/offline-eligibility`, {
      cache: 'no-store', headers: { Authorization: `Bearer ${owner.token}` },
    });
    assertOwner(owner);
    if (!response.ok) throw new Error('Offline access could not be verified. Keep this chart online and retry.');
    const body = await response.json();
    if (typeof body.offlineEligible !== 'boolean') throw new Error('Offline access could not be verified. Refresh Sena while online.');
    return rememberChartEligibility(clientReferenceId, body.offlineEligible);
  })().finally(() => checking.delete(key));
  checking.set(key, task);
  return task;
}
export async function requireChartOfflineEligibility(clientReferenceId, options) {
  const result = await checkChartEligibility(clientReferenceId, options);
  if (result?.eligible === false) throw eligibilityError();
  if (result?.eligible !== true) throw new Error('Connect to check this chart’s offline access before charting offline. Existing device changes are retained.');
  return result;
}
